import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Button, Grid, GridItem, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react'
import { getInstallationOptionById } from 'api'
import { EMC_FACES, getQuotePriceBreakdown, numberFormatter } from 'lib'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'

export const ModalPriceBreakdown = ({ quote, isOpen, onClose }) => {
  const [installationOption, setInstallationOption] = useState(null)

  useEffect(() => {
    if (!quote?.installationOptionId) {
      setInstallationOption(null)
      return
    }
    const load = async () => {
      try {
        const option = await getInstallationOptionById(quote.installationOptionId)
        setInstallationOption(option)
      } catch (err) {
        setInstallationOption(null)
      }
    }
    load()
  }, [quote?.installationOptionId])

  const priceBreakdown = useMemo(() => {
    return getQuotePriceBreakdown(quote, installationOption)
  }, [quote, installationOption])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="xl">Price Breakdown. Quote #{quote?.quoteNo}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Grid
            templateColumns="1fr 1fr"
            gap={5}
          >
            <GridItem
              gap="10px"
              display="flex"
              flexDirection="column"
            >
              <PriceRow label="EMC" price={priceBreakdown.emcCost} />
              {/* <PriceRow label="Monument" price={priceBreakdown.monumentCost} /> */}
              <PriceRow label="Shipping" price={priceBreakdown.shippingCost} />
              <PriceRow label="Tax" price={priceBreakdown.taxCost} />
              <PriceRow label="Discount" price={priceBreakdown.discount} />
            </GridItem>
            <GridItem
              gap="10px"
              display="flex"
              flexDirection="column"
            >
              <Accordion
                gap="10px"
                display="flex"
                flexDirection="column"
                allowMultiple
              >
                <AccordionItem border={0}>
                  <h2>
                    <AccordionButton justifyContent="space-between" p={0}>
                      <PriceRow w="100%" textAlign="left" label="Fabrication" price={priceBreakdown.fabricationCost + priceBreakdown.customColorCost} />
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} textAlign="right">
                    <PriceRow size="xs" label="Cabinet" price={quote?.cabinetCost} w="90%" ml="auto" />
                    <PriceRow size="xs" label="Pedestal" price={quote?.pedestalCost} w="90%" ml="auto" />
                    {(quote?.emcFace === EMC_FACES.SF && quote?.emcBackWrapChecked) && (
                      <PriceRow size="xs" label="Back Wrap" price={quote?.emcBackWrapCost} w="90%" ml="auto" />
                    )}
                    {((quote?.emcFace === EMC_FACES.DF || quote?.emcFace === EMC_FACES.DSF) && quote?.emcSideVentChecked) && (
                      <PriceRow size="xs" label="Side Vent" price={quote?.emcSideVentCost} w="90%" ml="auto" />
                    )}
                    {quote?.customColorChecked && (
                      <PriceRow size="xs" label="Custom Color" price={priceBreakdown.customColorCost} w="90%" ml="auto" />
                    )}
                  </AccordionPanel>
                </AccordionItem>
                <AccordionItem border={0}>
                  <h2>
                    <AccordionButton justifyContent="space-between" p={0}>
                      <PriceRow w="100%" textAlign="left" label="Steel" price={priceBreakdown.steelCost} />
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} textAlign="right">
                    <PriceRow size="xs" label="Vertical Support" price={quote?.verticalSupportCost} w="90%" ml="auto" />
                    {!!quote?.plateCost && (
                      <PriceRow size="xs" label="Plate Cost" price={quote?.plateCost} w="90%" ml="auto" />
                    )}
                    {!!quote?.footerStyleCost && (
                      <PriceRow size="xs" label="Footer Style" price={quote?.footerStyleCost} w="90%" ml="auto" />
                    )}
                  </AccordionPanel>
                </AccordionItem>
                <AccordionItem border={0}>
                  <h2>
                    <AccordionButton justifyContent="space-between" p={0}>
                      <PriceRow w="100%" textAlign="left" label="Install" price={priceBreakdown.installationCost + priceBreakdown.existingSignCost + priceBreakdown.siteSurveyCost} />
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} textAlign="right">
                    <PriceRow size="xs" label="Installation" price={priceBreakdown.installationCost - (quote?.mowCurbEnabled ? (quote?.mowCurbCost || 0) : 0)} w="90%" ml="auto" />
                    {!!quote?.mowCurbEnabled && (
                      <PriceRow size="xs" label="Mow Curb" price={quote?.mowCurbCost || 0} w="90%" ml="auto" />
                    )}
                    <PriceRow size="xs" label="Existing Sign" price={priceBreakdown.existingSignCost} w="90%" ml="auto" />
                    <PriceRow size="xs" label="Site Survey" price={priceBreakdown.siteSurveyCost} w="90%" ml="auto" />
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
              {!!priceBreakdown.permitCost && (
                <PriceRow label="Permit" price={priceBreakdown.permitCost} pr={5} />
              )}
              <PriceRow label="Overage" price={priceBreakdown.overage} pr={5} />
              <PriceRow label="Total" price={quote?.totalCost} pr={5} />
            </GridItem>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalPriceBreakdown.propTypes = {
  quote: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}

const PriceRow = ({ label, price, size = 'sm', ...rest }) => {
  return (
    <Grid templateColumns="1fr 1fr" columnGap={4} {...rest}>
      <GridItem>
        <Text fontSize={size}>{label} : </Text>
      </GridItem>
      <GridItem>
        <Text fontSize={size}>$ {numberFormatter(price)}</Text>
      </GridItem>
    </Grid>
  )
}

PriceRow.propTypes = {
  label: PropTypes.string,
  price: PropTypes.number,
  size: PropTypes.string
}
